import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import WorkingHoursEditor, { openDayCount, toRows, toWeek } from "./WorkingHoursEditor";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { saveWorkingHours } from "../services/branchService";

/**
 * The branch detail card: read the week, edit it, save it on its own.
 *
 * The grid itself lives in `WorkingHoursEditor`, shared with the create-branch
 * form. What stays here is what only this screen has — a saved branch to PUT
 * against, and a read-only summary for when nobody is editing.
 *
 * An empty week is still saveable from here, and deliberately: creating a
 * branch now requires at least one open day, but clearing an existing branch's
 * hours is how a mistake gets undone, and "not operating" is `status: closed`.
 */
export default function WorkingHours({ branchId, workingHours, onSaved }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [week, setWeek] = useState(() => toWeek(workingHours ?? []));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");

  // Re-sync whenever the branch reloads with a different schedule.
  useEffect(() => {
    setWeek(toWeek(workingHours ?? []));
  }, [workingHours]);

  const cancel = () => {
    setWeek(toWeek(workingHours ?? []));
    setError("");
    setErrors({});
    setEditing(false);
  };

  const save = async () => {
    setError("");
    setErrors({});
    setSaving(true);
    try {
      const saved = await saveWorkingHours(branchId, toRows(week));
      onSaved(saved);
      setEditing(false);
    } catch (err) {
      const fields = apiFieldErrors(err);
      setErrors(fields);
      const first = Object.values(fields).flat()[0];
      setError(first || apiErrorMessage(err, t("workingHours.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">
          {t("workingHours.title")}
          <span className="ms-2 rounded-full bg-raised px-2 py-1 text-xs text-ink-2">
            {t("workingHours.days", { open: openDayCount(week) })}
          </span>
        </h2>

        {editing ? (
          <div className="flex gap-2">
            <button type="button" onClick={cancel} disabled={saving} className="btn-secondary">
              {t("common.cancel")}
            </button>
            <button type="button" onClick={save} disabled={saving} className="btn-primary">
              {saving ? t("workingHours.saving") : t("common.save")}
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => setEditing(true)} className="btn-secondary">
            {t("workingHours.edit")}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      {editing ? (
        <WorkingHoursEditor week={week} onChange={setWeek} disabled={saving} errors={errors} />
      ) : (
        <div className="divide-y divide-line">
          {week.map((d) => (
            <div key={d.day} className="flex flex-wrap items-start gap-3 py-3">
              <div className="w-28 flex-shrink-0 pt-2 text-sm font-medium">
                {t(`days.${d.day}`)}
              </div>

              <div className="flex min-w-0 flex-1 flex-wrap gap-2 pt-1">
                {d.open ? (
                  d.ranges.map((range, index) => (
                    <span
                      key={index}
                      className="rounded-full bg-accent-wash px-3 py-1 text-xs font-medium text-accent-text"
                      dir="ltr"
                    >
                      {range.opens_at} – {range.closes_at}
                    </span>
                  ))
                ) : (
                  <span className="rounded-full bg-raised px-3 py-1 text-xs text-ink-2">
                    {t("common.closedLabel")}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
